import React from 'react';

import Router from 'next/router';
import { CommentButtonAction, PostActionButtons } from '@components/Feed/Post/ActionButtons';
import { LexicalDisplay } from '@components/Lexical/Display';
import {
  getFragment,
  TopArtistDropPostFragmentDoc,
  TopPlaylistPostFragmentDoc,
  TopPostInfoFragmentDoc,
  TopReleasePostFragmentDoc,
  type TopTrendingPostsQuery,
} from '@graphql/reactQuery';
import { useLexicalValidate } from '@hooks/useLexicalValidate';
import { trackClickEvent } from '@lib/analytics';
import { webappUriPath } from '@utils/url';
import { PlaylistPlayButton } from './PlaylistPlayButton';
import { ReleasePlayButton } from './ReleasePlayButton';
import { TopPostHeader } from './TopPostHeader';

export type TopPostFragment = NonNullable<
  NonNullable<TopTrendingPostsQuery['topTrendingPosts']>[number]
>;

export const TopPostSkeleton = () => {
  return (
    <div className="flex h-[148px] w-full animate-pulse flex-col gap-2">
      <div>
        <div className="h-[42px] w-[42px] rounded-full bg-neutral100" />
      </div>
      <div className="h-[18px] w-full rounded-sm bg-neutral100" />
      <div className="h-4 w-full rounded-sm bg-neutral100 pb-1" />
      <div className="-mx-2 -my-1 -ml-3 flex h-7 items-center gap-3 pl-3">
        <div className="h-6 w-[34px] bg-neutral100" />
        <div className="h-6 w-[34px] bg-neutral100" />
      </div>
    </div>
  );
};

export const TopPostInternal = ({
  postFrag,
  children,
}: {
  postFrag: TopPostFragment;
  children?: React.ReactNode;
}) => {
  const post = getFragment(TopPostInfoFragmentDoc, postFrag);

  const { webappUri, id: postId, creator, content, createdAt, lexicalContent } = post;

  const validatedLexicalContent = useLexicalValidate(lexicalContent);

  const onPostClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    if (e.target instanceof HTMLElement) {
      if (e.target.hasAttribute('data-feed-clickable-div')) {
        Router.push(webappUriPath(webappUri));
        trackClickEvent({
          event: 'Post Link Clicked',
          properties: {
            associationId: postId,
            associationType: 'Post',
            sourceComponent: 'Feed Post',
          },
        });
      }
    }
  };

  return (
    <div
      className="-m-3 cursor-pointer rounded-lg p-3 hover:bg-neutral50"
      data-feed-clickable-div
      onClick={onPostClick}
    >
      <div className="flex flex-col gap-2" data-feed-clickable-div>
        <TopPostHeader
          userFrag={creator}
          timestamp={new Date(createdAt).getTime()}
          showFeedCuratorBadge
          className="!gap-2"
        />
        <div data-feed-clickable-div>
          {validatedLexicalContent ? (
            <LexicalDisplay
              state={validatedLexicalContent}
              className="line-clamp-3 w-full text-base-m !text-base700"
            />
          ) : (
            <div className="line-clamp-3 w-full text-base-m text-base700">{content}</div>
          )}
        </div>
        {children}
        <PostActionButtons
          postFrag={postFrag}
          commentButtonAction={CommentButtonAction.ShowModal}
          resourceOwnerTwitterHandle={null}
          sourceLocation="Post Feed Card"
          hideShareButton
          containerClassName="pl-3 -ml-3 gap-3"
          buttonClassName="p-1 -ml-1 !text-base-s"
          iconSize={14}
          source="Feed Card"
        />
      </div>
    </div>
  );
};

export function TopPost({ postFrag }: { postFrag: TopPostFragment }) {
  if (postFrag.__typename === 'ReleasePost') {
    const { optionalRelease: release } = getFragment(TopReleasePostFragmentDoc, postFrag);

    return (
      <TopPostInternal postFrag={postFrag}>
        <ReleasePlayButton release={release} />
      </TopPostInternal>
    );
  }

  if (postFrag.__typename === 'ArtistDropPost') {
    const { release } = getFragment(TopArtistDropPostFragmentDoc, postFrag);

    return (
      <TopPostInternal postFrag={postFrag}>
        <ReleasePlayButton release={release} />
      </TopPostInternal>
    );
  }

  if (postFrag.__typename === 'PlaylistPost') {
    const { optionalShelf: playlist } = getFragment(TopPlaylistPostFragmentDoc, postFrag);

    return (
      <TopPostInternal postFrag={postFrag}>
        <PlaylistPlayButton playlist={playlist} />
      </TopPostInternal>
    );
  }

  return <TopPostInternal postFrag={postFrag} />;
}
